import React, { useContext } from 'react';
import styled from 'styled-components';

import Icon from 'kingpin/atoms/Icon';
import Tooltip from 'components/Tooltip';
import FeatureGate, { FEATURE_GATES } from 'components/FeatureGate';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import useInsightsChatBudget from 'hooks/useInsightsChatBudget';

// What the agent is called in Studio, where it builds rather than answers
export const BUILD_WITH_AI_LABEL = 'Build with AI';

// A square icon button matching the Share button's boundaries (32×32, 10px
// radius, solid fill), framed by the same colourful gradient border as the
// top-level Insights nav button (var(--color-ai-gradient)). The ring is a
// masked ::before so it follows the rounded corners as a crisp 1px frame while
// the solid fill keeps the interior clean.
const GradientIconButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 10px;
  background-color: var(--color-bg-primary);
  cursor: pointer;
  transition:
    transform 120ms ease,
    background-color 150ms ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 10px;
    padding: 1px;
    background: var(--color-ai-gradient);
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  &:hover {
    background-color: var(--color-bg-hover);
  }

  &:active {
    transform: translateY(1px);
  }
`;

// Opens the AI Insights slideout from a Dashboard/Report toolbar. The chat
// auto-scopes to the currently-viewed content (published via
// useRegisterInsightsContent), so no props are needed. Gated by the same
// account feature gate + per-user budget eligibility as the top-level entry.
const EligibleButton = ({
  icon,
  label,
}: {
  icon: Kingpin.Icon;
  label: string;
}) => {
  const { isEligible } = useInsightsChatBudget();
  const { open } = useContext(InsightsChatContext);

  if (!isEligible) {
    return null;
  }

  return (
    <Tooltip content={label}>
      <GradientIconButton
        type="button"
        onClick={() => open()}
        data-testid="open-insights-button"
      >
        <Icon
          icon={icon}
          width={16}
          height={16}
          color="var(--color-text-secondary)"
        />
      </GradientIconButton>
    </Tooltip>
  );
};

// Studio shows a wrench with sparkles, named "Build with AI", since the
// agent builds there
const OpenInsightsButton = ({
  icon = 'sparkles',
  label = 'Insights',
}: {
  icon?: Kingpin.Icon;
  label?: string;
}) => {
  return (
    <FeatureGate featureName={FEATURE_GATES.AI_INSIGHTS_CHAT}>
      <EligibleButton icon={icon} label={label} />
    </FeatureGate>
  );
};

export default OpenInsightsButton;
